import { action, computed, observable, runInAction } from 'mobx';
import { ISelectOption } from 'telia-front-react';
import { API, FetchStatus } from '../util/ApiUtil';
import { DealerRepresentationStore } from './DealerRepresentationStore';
import RootStore from './RootStore';
import TranslateStore from './TranslateStore';

export const EMPTY_VALUE = 'EMPTY';

export interface DealerInfo {
  name?: string;
  department?: string;
}

export class DealerPreferencesStore {
  @observable accessor dealerInfo: DealerInfo | undefined;
  @observable accessor profile: string | null = null;
  @observable accessor group: string | null = null;
  @observable accessor profiles: ISelectOption[] = [];
  @observable accessor groups: ISelectOption[] = [];
  @observable accessor submitStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor loadStatus: FetchStatus = FetchStatus.UNINITIALIZED;

  protected dealerRepresentationStore: DealerRepresentationStore;
  protected translateStore: TranslateStore;

  constructor(rootStore: RootStore) {
    this.dealerRepresentationStore = rootStore.dealerRepresentationStore;
    this.translateStore = rootStore.translateStore;
  }

  public async init() {
    this.loadStatus = FetchStatus.FETCHING;
    try {
      await Promise.all([this.getProfileOptions(), this.getGroupOptions(), this.dealerRepresentationStore.init()]);
      this.initializeDealerPreferences();
      this.setDealerInfo();
      runInAction(() => {
        this.loadStatus = FetchStatus.FETCHED;
      });
    } catch (e) {
      runInAction(() => {
        this.loadStatus = FetchStatus.ERROR;
      });
    }
  }

  public async updateDealerPreferences(): Promise<any> {
    this.submitStatus = FetchStatus.FETCHING;
    const group = this.group === EMPTY_VALUE ? null : this.group;
    const profile = this.profile === EMPTY_VALUE ? null : this.profile;
    try {
      await API.put('/dealer/preferences', {
        groupGUID: group,
        profile
      });
      runInAction(() => {
        this.submitStatus = FetchStatus.FETCHED;
        this.sendPostMessage(group, profile);
      });
    } catch (e) {
      runInAction(() => {
        this.submitStatus = FetchStatus.ERROR;
      });
    }
  }

  @action.bound
  public setGroup(group: string) {
    this.group = group;
    this.clearSubmitStatus();
  }

  @action.bound
  public setProfile(profile: string) {
    this.profile = profile;
    this.clearSubmitStatus();
  }

  @computed
  public get isLoading() {
    return this.loadStatus === FetchStatus.FETCHING;
  }

  @computed
  public get isLoadError() {
    return this.loadStatus === FetchStatus.ERROR;
  }

  @computed
  public get isSubmitting() {
    return this.submitStatus === FetchStatus.FETCHING;
  }

  @computed
  public get isSubmitted() {
    return this.submitStatus === FetchStatus.FETCHED;
  }

  @computed
  public get submitError() {
    return this.submitStatus === FetchStatus.ERROR;
  }

  private async getProfileOptions(): Promise<any> {
    const response = await API.get('/dealer/preferences/profiles');
    this.setProfiles(response.data.profiles);
  }

  private async getGroupOptions(): Promise<any> {
    const response = await API.get('/dealer/preferences/groups');
    this.setGroups(response.data.groups);
  }

  private getDealerPreferences(): Promise<any> {
    return API.get('/dealer/preferences');
  }

  @action.bound
  private setProfiles(profiles: ISelectOption[]) {
    const { translate } = this.translateStore;
    this.profiles = profiles;
    this.profiles.unshift({ value: EMPTY_VALUE, label: translate('dealer.profiles.EMPTY') });
  }

  @action.bound
  private setGroups(groups: ISelectOption[]) {
    const { translate } = this.translateStore;
    this.groups = groups;
    this.groups.unshift({ value: EMPTY_VALUE, label: translate('dealer.groups.EMPTY') });
  }

  @action.bound
  private async initializeDealerPreferences() {
    const response = await this.getDealerPreferences();
    this.profile = this.getValueFromArrayOrDefault(this.profiles, response.data.profile, EMPTY_VALUE);
    this.group = this.getValueFromArrayOrDefault(this.groups, response.data.groupGUID, EMPTY_VALUE);
  }

  private getValueFromArrayOrDefault(options: ISelectOption[], value: string, defaultValue: string) {
    return this.getOptionByValue(options, value)?.value || defaultValue;
  }

  private getOptionByValue(options: ISelectOption[], value: string) {
    return options.find((option: ISelectOption) => option.value === value);
  }

  @action.bound
  private setDealerInfo() {
    this.dealerInfo = {
      name: this.dealerRepresentationStore.dealer?.displayName,
      department: this.dealerRepresentationStore.dealer?.department
    };
  }

  private sendPostMessage(group: string | null, profile: string | null) {
    const groupObject = group
      ? {
          groupGUID: group,
          name: this.getOptionByValue(this.groups, group)?.label
        }
      : null;
    const targetWindow = window.parent;
    targetWindow.postMessage(
      {
        action: 'save-dealer-preferences',
        data: {
          group: groupObject,
          profile: profile !== EMPTY_VALUE ? profile : null
        }
      },
      '*'
    );
  }

  @action.bound
  private clearSubmitStatus() {
    this.submitStatus = FetchStatus.UNINITIALIZED;
  }
}
